/**
 * RedeemCode — /redeem
 * User pastes an access code (HomeLyph → Pro, D-Freeze → Business), we lock it
 * server-side and upgrade their role for 90 days. On success we show a
 * confirmation card with the expiry date.
 */
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import ToolSelector from '../components/ToolSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Ticket, Loader2, CheckCircle2, Sparkles } from 'lucide-react';

const RedeemCode = () => {
  const { API, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) { toast.error('Enter a code'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/access-codes/redeem`, { code: code.trim().toUpperCase() });
      setResult(data);
      // Refresh user in AuthContext so nav + gated features update immediately.
      try {
        const me = await axios.get(`${API}/auth/me`);
        setUser(me.data);
      } catch { /* ignore */ }
      toast.success(`Welcome to ${data.product_name}! You now have ${data.tier} access.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Redemption failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="redeem-page">
      <ToolSelector />
      <div className="max-w-lg mx-auto px-4 md:px-6 py-8">
        {result ? (
          <div className="pro-card p-6 text-center border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent" data-testid="redeem-success">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary text-primary-foreground mb-4">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <h1 className="font-heading text-3xl font-extrabold">{result.product_name} activated!</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              You now have <span className="font-bold text-foreground">{result.tier}</span> access
              for the next {result.days} days.
            </p>
            <div className="mt-4 text-xs text-muted-foreground">
              Access expires <span className="font-bold text-foreground">{result.expires_at?.slice(0, 10)}</span>. We&apos;ll remind you a week before with renewal options.
            </div>
            <Button onClick={() => navigate('/dashboard')} className="mt-5 rounded-full font-bold" data-testid="redeem-go-dashboard">
              Take me in <Sparkles className="ml-1.5 w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <div className="pro-card p-6" data-testid="redeem-form">
            <div className="eyebrow text-primary inline-flex items-center gap-1.5">
              <Ticket className="w-3 h-3" /> Redeem access code
            </div>
            <h1 className="font-heading text-3xl font-extrabold mt-1.5">Unlock Pro or Business</h1>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Have an access code? Enter it below to unlock time-limited Pro or Business access. Codes are single-use.
            </p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Your code</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XX-XXXX-XXXX-XXXX"
                className="font-mono uppercase tracking-widest text-lg"
                data-testid="redeem-code-input"
              />
              <Button type="submit" disabled={submitting || !code.trim()} className="rounded-full font-bold w-full" data-testid="redeem-submit">
                {submitting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Verifying…</> : <>Activate access <Ticket className="ml-1.5 w-3.5 h-3.5" /></>}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedeemCode;
